<template>
    <div class="grid grid-cols-2 gap-8">
        <section>
            <h3 class="mb-4 text-lg font-medium">Non-Grouped</h3>
            <div class="flex flex-col gap-4">
                <h4 class="text-sm text-content-secondary">With Search</h4>
                <DsSingleSelect
                    v-model="selectedStatusSearch"
                    id="status"
                    :error="statusSearchHasError"
                    :label="labelStatus"
                    :options="statusOptions"
                    :placeholder="placeholderStatus"
                    required
                    @close="handleStatusSearchClose"
                >
                    <template #optionLabelLeading>
                        <DsIcon
                            icon="fa-regular fa-user"
                            size="md"
                        />
                    </template>
                    <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Status>">
                        <span class="break-all">{{ option.name }}</span>
                    </template>
                    <template #error>
                        <p class="text-sm text-content-error">This field is required</p>
                    </template>
                </DsSingleSelect>

                <h4 class="text-sm text-content-secondary">Without Search</h4>
                <DsSingleSelect
                    v-model="selectedStatus"
                    id="status"
                    :error="statusHasError"
                    :label="labelStatus"
                    :options="statusOptions.slice(0, 4)"
                    :placeholder="placeholderStatus"
                    required
                    @close="handleStatusClose"
                >
                    <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Status>">
                        <span class="break-all">{{ option.name }}</span>
                    </template>
                    <template #error>
                        <p class="text-sm text-content-error">This field is required</p>
                    </template>
                </DsSingleSelect>

                <h4 class="text-sm text-content-secondary">Disabled</h4>
                <DsSingleSelect
                    v-model="selectedStatus"
                    id="status-disabled"
                    disabled
                    :label="labelStatus"
                    :options="statusOptions"
                    :placeholder="placeholderStatus"
                />

                <h4 class="text-sm text-content-secondary">With Disabled Options</h4>
                <DsSingleSelect
                    v-model="selectedStatusWithDisabled"
                    id="status-with-disabled"
                    helperText="Some status options are disabled and cannot be selected"
                    :label="labelStatus"
                    :options="statusOptionsWithDisabled"
                    :placeholder="placeholderStatus"
                >
                    <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Status>">
                        <span class="break-all">{{ option.name }}</span>
                    </template>
                </DsSingleSelect>
            </div>
        </section>

        <section>
            <h3 class="mb-4 text-lg font-medium">Grouped</h3>
            <div class="flex flex-col gap-4">
                <h4 class="text-sm text-content-secondary">With Search</h4>
                <DsSingleSelect
                    v-model="selectedCountrySearch"
                    id="country"
                    groupLabel="continent"
                    helperText="This is a helper text"
                    :label="labelCountry"
                    :options="countryOptions"
                    :placeholder="placeholderCountry"
                >
                    <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Country>">
                        <span>{{ option.name }}</span>
                    </template>
                </DsSingleSelect>

                <h4 class="text-sm text-content-secondary">Without Search</h4>
                <DsSingleSelect
                    v-model="selectedCountry"
                    id="country"
                    groupLabel="continent"
                    :label="labelCountry"
                    :options="countryOptions.slice(0, 4)"
                    :placeholder="placeholderCountry"
                >
                    <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Country>">
                        <span>{{ option.name }}</span>
                    </template>
                </DsSingleSelect>
            </div>
        </section>

        <section class="col-span-2">
            <h3 class="mb-4 text-lg font-medium">Empty State</h3>
            <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <h4 class="text-sm text-content-secondary">Default empty state</h4>
                    <DsSingleSelect
                        v-model="selectedEmptyDefault"
                        id="empty-default"
                        :label="labelWireDoc"
                        :options="[]"
                        :placeholder="placeholderWireDoc"
                    />
                </div>

                <div class="flex flex-col gap-2">
                    <h4 class="text-sm text-content-secondary">Custom empty state slot</h4>
                    <DsSingleSelect
                        v-model="selectedEmptyCustom"
                        id="empty-custom"
                        :label="labelWireDoc"
                        :options="[]"
                        :placeholder="placeholderWireDoc"
                    >
                        <template #emptyState>
                            <DsSelectEmptyState
                                icon="fa-regular fa-file-invoice-dollar"
                                subtitle="Once added, all wire instructions will be listed here."
                                title="No wire instructions yet"
                            />
                        </template>
                    </DsSingleSelect>
                </div>
            </div>
        </section>

        <section class="col-span-2">
            <h3 class="mb-4 text-lg font-medium">Modal with Single Select</h3>
            <div class="flex flex-col gap-4">
                <p class="text-sm text-content-secondary">
                    Click the button below to open a modal containing a single select component.
                </p>

                <DsButton
                    size="md"
                    variant="primary"
                    @click="openModal = true"
                >
                    Open Modal with Single Select
                </DsButton>

                <div
                    v-if="selectedModalStatus"
                    class="text-sm text-content-secondary"
                >
                    <strong>Selected in modal:</strong> {{ selectedModalStatus.name }}
                </div>
            </div>

            <Teleport to="body">
                <DsDialog
                    v-model:open="openModal"
                    class="modal-with-singleselect"
                    primary-action="Confirm"
                    secondary-action="Cancel"
                    title="Select Status"
                    @click-primary="handleModalConfirm"
                    @click-secondary="openModal = false"
                >
                    <div class="mb-6">
                        <p class="mb-4 text-content-secondary">
                            This modal demonstrates how a single select component can be used within a modal dialog. The single
                            select maintains all its functionality including search, validation, and accessibility features.
                        </p>

                        <DsSingleSelect
                            v-model="selectedModalStatus"
                            id="modal-status"
                            :error="modalStatusError"
                            :label="modalStatusLabel"
                            :options="statusOptions"
                            :placeholder="modalStatusPlaceholder"
                            required
                            @close="handleModalStatusClose"
                        >
                            <template #optionLabelLeading>
                                <DsIcon
                                    icon="fa-regular fa-user"
                                    size="md"
                                />
                            </template>
                            <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Status>">
                                <span class="break-all">{{ option.name }}</span>
                            </template>
                        </DsSingleSelect>
                    </div>
                </DsDialog>
            </Teleport>
        </section>

        <section class="col-span-2">
            <h3 class="mb-4 text-lg font-medium">Sheet with Single Select</h3>
            <div class="flex flex-col gap-4">
                <p class="text-sm text-content-secondary">
                    Click the button below to open a sheet containing a single select component. Sheets slide in from the right
                    and are great for forms and detailed content.
                </p>

                <DsButton
                    size="md"
                    variant="primary"
                    @click="openSheet = true"
                >
                    Open Sheet with Single Select
                </DsButton>

                <div
                    v-if="selectedSheetStatus"
                    class="text-sm text-content-secondary"
                >
                    <strong>Selected in sheet:</strong> {{ selectedSheetStatus.name }}
                </div>
            </div>

            <Teleport to="body">
                <DsSheet
                    v-model:open="openSheet"
                    primary-action="Confirm"
                    secondary-action="Cancel"
                    size="md"
                    title="Select Status"
                    @click-primary="handleSheetConfirm"
                    @click-secondary="openSheet = false"
                >
                    <div class="mb-6">
                        <p class="mb-4 text-content-secondary">
                            This sheet demonstrates how a single select component can be used within a sliding sheet dialog. The
                            single select maintains all its functionality including search, validation, and accessibility
                            features. Sheets are particularly useful for mobile interfaces and when you need more space for form
                            content.
                        </p>

                        <DsSingleSelect
                            v-model="selectedSheetStatus"
                            id="sheet-status"
                            :error="sheetStatusError"
                            :label="sheetStatusLabel"
                            :options="statusOptions"
                            :placeholder="sheetStatusPlaceholder"
                            required
                            @close="handleSheetStatusClose"
                        >
                            <template #optionLabelLeading>
                                <DsIcon
                                    icon="fa-regular fa-user"
                                    size="md"
                                />
                            </template>
                            <template #optionLabelMain="{ option }: DsSingleSelectSlotProps<Status>">
                                <span class="break-all">{{ option.name }}</span>
                            </template>
                        </DsSingleSelect>
                    </div>
                </DsSheet>
            </Teleport>
        </section>
    </div>
</template>

<script setup lang="ts">
import DsDialog from '@/components/design-system/DsDialog.vue';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsSelectEmptyState from '@/components/design-system/DsSelectEmptyState.vue';
import DsSheet from '@/components/design-system/DsSheet.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsSingleSelect from '@/components/design-system/singleselect/DsSingleSelect.vue';
import type { DsSingleSelectSlotProps } from '@/components/design-system/singleselect/single-select.types';
import { computed, ref } from 'vue';

interface Status {
    id: string;
    name: string;
    disabled?: boolean;
}

interface Country {
    id: string;
    name: string;
    continent: string;
}

interface WireDoc {
    id: string;
}

const labelStatus = ref('Status');
const labelCountry = ref('Country');
const labelWireDoc = ref('Wire instructions');

const placeholderStatus = ref('Select status');
const placeholderCountry = ref('Select country');
const placeholderWireDoc = ref('Select wire instructions');

const selectedStatus = ref<Status>();
const selectedStatusSearch = ref<Status>();
const selectedStatusWithDisabled = ref<Status>();

const selectedCountry = ref<Country>();
const selectedCountrySearch = ref<Country>();

const selectedEmptyDefault = ref<WireDoc>();
const selectedEmptyCustom = ref<WireDoc>();

const handleStatusClose = () => {
    if (!selectedStatus.value) {
        statusHasError.value = true;
    } else {
        statusHasError.value = false;
    }
};

const handleStatusSearchClose = () => {
    if (!selectedStatusSearch.value) {
        statusSearchHasError.value = true;
    } else {
        statusSearchHasError.value = false;
    }
};

const statuses = ref<Status[]>([
    {
        id: '1',
        name: 'Drafts',
    },
    {
        id: '2',
        name: 'Needs Review',
    },
    {
        id: '3',
        name: 'Pending',
    },
    {
        id: '4',
        name: 'Rejected',
    },
    {
        id: '5',
        name: 'Approved',
    },
    {
        id: '6',
        name: 'Cancelled',
    },
]);

const countries = ref<Country[]>([
    {
        id: '1',
        name: 'United States',
        continent: 'North America',
    },
    {
        id: '2',
        name: 'Canada',
        continent: 'North America',
    },
    {
        id: '3',
        name: 'United Kingdom',
        continent: 'Europe',
    },
    {
        id: '4',
        name: 'France',
        continent: 'Europe',
    },
    {
        id: '5',
        name: 'Germany',
        continent: 'Europe',
    },
    {
        id: '6',
        name: 'Brazil',
        continent: 'South America',
    },
    {
        id: '7',
        name: 'Argentina',
        continent: 'South America',
    },
    {
        id: '8',
        name: 'Japan',
        continent: 'Asia',
    },
    {
        id: '9',
        name: 'China',
        continent: 'Asia',
    },
]);

const statusOptions = computed(() => {
    return statuses.value.map(status => ({
        ...status,
        id: status.id,
        value: status.name.toLowerCase(),
        label: status.name,
    }));
});

const statusOptionsWithDisabled = computed(() => {
    return statuses.value.map(status => ({
        ...status,
        id: status.id,
        value: status.name.toLowerCase(),
        label: status.name,
        disabled: status.name === 'Inactive' || status.name === 'Pending', // Disable some options
    }));
});

const countryOptions = computed(() => {
    return countries.value.map(country => ({
        ...country,
        id: country.id,
        value: country.name.toLowerCase(),
        label: country.name,
    }));
});

const statusSearchHasError = ref<string | boolean>(false);
const statusHasError = ref<string | boolean>(false);

const openModal = ref(false);
const selectedModalStatus = ref<Status | undefined>(undefined);
const modalStatusError = ref<string | boolean>('');

const modalStatusLabel = ref('Status');
const modalStatusPlaceholder = ref('Select a status');

const handleModalStatusClose = () => {
    if (!selectedModalStatus.value) {
        modalStatusError.value = 'This field is required';
    } else {
        modalStatusError.value = '';
    }
};

const handleModalConfirm = () => {
    if (!selectedModalStatus.value) {
        modalStatusError.value = 'This field is required';
        return;
    }

    openModal.value = false;
    modalStatusError.value = '';
};

const openSheet = ref(false);
const selectedSheetStatus = ref<Status | undefined>(undefined);
const sheetStatusError = ref<string | boolean>('');

const sheetStatusLabel = ref('Status');
const sheetStatusPlaceholder = ref('Select a status');

const handleSheetStatusClose = () => {
    if (!selectedSheetStatus.value) {
        sheetStatusError.value = 'This field is required';
    } else {
        sheetStatusError.value = '';
    }
};

const handleSheetConfirm = () => {
    if (!selectedSheetStatus.value) {
        sheetStatusError.value = 'This field is required';
        return;
    }

    openSheet.value = false;
    sheetStatusError.value = '';
};
</script>
